import { profileStorageKey } from './profileStorageKey';
import { isOpeningTimesWeekdayKey } from './openingTimesWeekdays';

const STORAGE_PREFIX = 'ofrontend.assetCreateLastAttempt.v3';

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function assetCreateAttemptStorageKey(loginId?: string | null): string {
  return profileStorageKey(STORAGE_PREFIX, loginId);
}

/**
 * Keep weekday opening hours only — drop blackouts and specific-date overrides so a
 * reused “last create” draft does not carry previous one-off dates.
 */
export function stripOpeningTimesDateOverridesFromAssetBody(
  body: Record<string, unknown>,
): Record<string, unknown> {
  const next = { ...body };
  delete next.blackout_ranges;
  const ot = next.opening_times;
  if (!isPlainObject(ot)) {
    return next;
  }
  const weekdaysOnly: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(ot)) {
    if (isOpeningTimesWeekdayKey(key)) {
      weekdaysOnly[key] = value;
    }
  }
  next.opening_times = weekdaysOnly;
  return next;
}

/** Last create-asset form body for this asset owner (device-local). */
export function loadPersistedAssetCreateAttempt(
  loginId?: string | null,
): Record<string, unknown> | null {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  try {
    const raw = localStorage.getItem(assetCreateAttemptStorageKey(loginId));
    if (!raw?.trim()) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    return isPlainObject(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function persistAssetCreateAttempt(
  body: Record<string, unknown>,
  loginId?: string | null,
): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  try {
    localStorage.setItem(assetCreateAttemptStorageKey(loginId), JSON.stringify(body));
  } catch {
    /* quota exceeded, private browsing, etc. */
  }
}

export function clearPersistedAssetCreateAttempt(loginId?: string | null): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  try {
    localStorage.removeItem(assetCreateAttemptStorageKey(loginId));
  } catch {
    /* ignore */
  }
}
